import { useCallback, useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  formatRoute,
  SUBSCRIPTION_FEED_FIRST_PAGE,
  SUBSCRIPTION_FEED_PAGE_SIZE,
  type SubscriptionFeedPage,
} from '@visious/core';
import { AUTH_QUERY_KEY, fetchSubscriptionFeed, type AuthApiProblem } from '../../api/auth';
import { loadSubscriptions, subscriptionsListKey } from '../../subscriptions/list';
import { useWatchProgressLookup } from '../../history/useWatchProgress';
import { systemClock } from '../../platform/clock';
import { getPlatform } from '../../platform/instance';
import { useSession } from '../../platform/session';
import { useSettings, writeSettings } from '../../platform/settings';
import { subscriptionsFeedState, type SubscriptionsFeedState } from './model';

/**
 * The React binding for §8's Subscriptions page. Every decision is in `model.ts` and in core; what is
 * here is the two queries and the clock. The channel list's lazy-avatar machinery moved to the manage
 * page (`screens/channels/`) with the strip that used it — what stays of the list here is its *count*,
 * which the empty states need to tell "no subscriptions" from "no videos yet".
 *
 * **`useInfiniteQuery` for the feed, for `useChannel.ts`'s reason**: one list, one identity,
 * append-only, and the cursor is a number the previous page determines. `retry: false` for 1c's:
 * §3's retry and backoff live in `createApiTransport`.
 *
 * **Every key starts with `AUTH_QUERY_KEY`** — 3a's convention with teeth. Signing out removes that
 * prefix from the cache, so a feed cached under any other key would keep rendering after the
 * credential is gone.
 */

export interface SubscriptionsBinding {
  readonly feed: SubscriptionsFeedState;
  readonly loadMore: () => void;
  readonly loadingMore: boolean;
  readonly retry: () => void;
  readonly refreshing: boolean;
  /** The manage page — the "Manage" button in the header. */
  readonly manageHref: string;
  /** The seen filter — a setting, so it survives the visit (`hideWatchedInFeed`). */
  readonly hideSeen: boolean;
  readonly setHideSeen: (next: boolean) => void;
  /** The grid search — display-only, so it is state here and never in the URL. */
  readonly search: string;
  readonly setSearch: (value: string) => void;
}

/** A query that failed is a value here, not a throw — the transport returns its errors (1c). */
type FeedPageResult = { readonly ok: true; readonly page: SubscriptionFeedPage } | { readonly ok: false; readonly problem: AuthApiProblem };

/**
 * `null` scopes, deliberately — the same call the sign-in page makes and for the same reason it gives:
 * a page that pre-refuses its own request reports a scope gap it *inferred* rather than one that
 * happened. `authGet`'s client-side check exists for a caller that knows the grant exactly; here the
 * instance is the authority, and `api/problem.ts` already has a sentence for each of the five ways it
 * says no. The cost of being wrong in this direction is one refused request; in the other it is a page
 * that refuses to load against a grant that would have worked.
 */
async function loadFeedPage(page: number, pageSize: number): Promise<FeedPageResult> {
  const boot = await getPlatform();
  if (!boot.ok) return { ok: false, problem: { kind: 'bad-url', path: '/api/v1/auth/feed' } };

  const result = await fetchSubscriptionFeed(boot.platform, page, pageSize, null);
  return result.ok ? { ok: true, page: result.value } : { ok: false, problem: result.error };
}

export function useSubscriptions(): SubscriptionsBinding {
  const session = useSession();
  /*
   * The grant's own timestamp is part of the identity, which the sign-in page established: a failure
   * is a *value* here rather than a thrown error, so a refusal caused by the previous token would
   * otherwise sit in the cache and be served to the new one. Re-authorizing writes a new
   * `grantedAtEpochSec`, so the key moves with the credential.
   */
  const grantKey = session.grantedAtEpochSec ?? 0;
  const hideSeen = useSettings().hideWatchedInFeed;
  const [search, setSearch] = useState('');

  const feedQuery = useInfiniteQuery({
    queryKey: [AUTH_QUERY_KEY, 'subscriptionFeed', grantKey],
    initialPageParam: SUBSCRIPTION_FEED_FIRST_PAGE,
    queryFn: ({ pageParam }) => loadFeedPage(pageParam, SUBSCRIPTION_FEED_PAGE_SIZE),
    getNextPageParam: (_last, all) => all.length + SUBSCRIPTION_FEED_FIRST_PAGE,
    retry: false,
    // A subscription feed is what is new, so it goes stale quickly — but not so quickly that opening a
    // video and pressing Back re-fetches 52 KB to show the same rows.
    staleTime: 5 * 60 * 1000,
    enabled: session.ready && session.facts.signedIn,
  });

  const subsQuery = useQuery({
    queryKey: subscriptionsListKey(grantKey),
    queryFn: () => loadSubscriptions(),
    retry: false,
    // The set of channels you follow changes far less often than what they publish.
    staleTime: 30 * 60 * 1000,
    enabled: session.ready && session.facts.signedIn,
  });

  const feedProblem = firstProblem(feedQuery.data?.pages, feedQuery.error);
  const subscriptions = subsQuery.data?.ok === true ? subsQuery.data.subscriptions : null;

  const pages = useMemo(
    () => successfulPages(feedQuery.data?.pages),
    [feedQuery.data?.pages],
  );

  const progress = useWatchProgressLookup();

  const feed = useMemo(
    () =>
      subscriptionsFeedState({
        pages: feedQuery.isPending ? null : pages,
        problem: feedProblem,
        subscriptionCount: subscriptions?.length ?? null,
        pageSize: SUBSCRIPTION_FEED_PAGE_SIZE,
        // As of when the rows *arrived*, not this render — 2b's rule: a page left open would otherwise
        // creep from "2 hours ago" to "3 hours ago" while the list itself is an hour stale.
        nowEpochSec: Math.floor(
          (feedQuery.dataUpdatedAt > 0 ? feedQuery.dataUpdatedAt : systemClock.nowEpochMs()) / 1000,
        ),
        progress,
        hideSeen,
        search,
      }),
    [
      feedQuery.dataUpdatedAt,
      feedQuery.isPending,
      feedProblem,
      hideSeen,
      pages,
      progress,
      search,
      subscriptions,
    ],
  );

  const nextPage = feed.kind === 'ready' ? feed.nextPage : null;

  return {
    feed,
    // Guarded by the model's own answer rather than by React Query's `hasNextPage`, which cannot know:
    // `nextSubscriptionFeedPage` reads the last page's *size* and that is the whole terminating proof.
    loadMore: () => {
      if (nextPage !== null && !feedQuery.isFetchingNextPage) void feedQuery.fetchNextPage();
    },
    loadingMore: feedQuery.isFetchingNextPage,
    retry: () => {
      void feedQuery.refetch();
      void subsQuery.refetch();
    },
    refreshing: feedQuery.isFetching && !feedQuery.isPending && !feedQuery.isFetchingNextPage,
    manageHref: MANAGE_HREF,
    hideSeen,
    setHideSeen: useCallback((next: boolean) => {
      writeSettings({ hideWatchedInFeed: next });
    }, []),
    search,
    setSearch,
  };
}

const MANAGE_HREF = formatRoute({ kind: 'feed', feed: 'channels' });

const NETWORK_PROBLEM: AuthApiProblem = {
  kind: 'transport',
  error: { kind: 'network', status: null, url: '', retryable: true },
};

/**
 * The first page that failed, or `null`.
 *
 * A *later* page failing is deliberately treated as a page problem too rather than as a load-more
 * error, because the alternative is worse: the rows already on screen would stay, with a silent
 * button that does nothing. There is no such case in practice — the transport classifies and this
 * endpoint is stable — but "silently does nothing" is the failure mode to design against.
 */
function firstProblem(
  pages: readonly FeedPageResult[] | undefined,
  error: unknown,
): AuthApiProblem | null {
  if (error !== null && error !== undefined) return NETWORK_PROBLEM;
  for (const page of pages ?? []) if (!page.ok) return page.problem;
  return null;
}

function successfulPages(pages: readonly FeedPageResult[] | undefined): readonly SubscriptionFeedPage[] {
  return (pages ?? []).flatMap((page) => (page.ok ? [page.page] : []));
}
